import React from 'react';
import { Card, CardHeader, CardBody, Text, Flex, Box } from '@backstage/ui';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { RELATION_OWNER_OF, Entity } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import {
  RiBriefcaseLine,
  RiCodeBoxLine,
  RiPlug2Line,
  RiDatabase2Line,
  RiApps2Line,
  RiStackLine,
} from '@remixicon/react';
import { EmptyState, Tile } from '../../../../shared/components';
import styles from './profiles.module.css';

const kindIcons: Record<string, React.ReactNode> = {
  component: <RiCodeBoxLine size={14} />,
  api: <RiPlug2Line size={14} />,
  resource: <RiDatabase2Line size={14} />,
  system: <RiApps2Line size={14} />,
};

const OwnershipContent = ({
  entities,
  loading,
  error,
}: {
  entities: Entity[] | undefined;
  loading: boolean;
  error: Error | undefined;
}) => {
  const { t } = useTranslationRef(appTranslationRef);

  if (loading) {
    return (
      <Flex direction="column" gap="3">
        {[1, 2, 3].map(i => (
          <Box key={i} className={styles.skeleton} style={{ height: '50px' }} />
        ))}
      </Flex>
    );
  }
  if (error) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('ownership.error')}
      </Text>
    );
  }
  if (!entities || entities.length === 0) {
    return <EmptyState title={t('ownership.empty')} />;
  }

  const groupedByKind: Record<string, Entity[]> = {};
  for (const e of entities) {
    const kind = e.kind.toLowerCase();
    if (!groupedByKind[kind]) groupedByKind[kind] = [];
    groupedByKind[kind]!.push(e);
  }

  return (
    <Flex direction="column" gap="4">
      {/* Summary stat pills */}
      <div className={styles.statPills}>
        {Object.entries(groupedByKind).map(([kind, items]) => (
          <div key={kind} className={styles.statPill}>
            {kindIcons[kind] || <RiStackLine size={14} />}
            <Text variant="body-x-small" className={styles.statPillCount}>
              {items.length}
            </Text>
            <Text variant="body-x-small" color="secondary">
              {kind}
            </Text>
          </div>
        ))}
      </div>

      {/* Entity grid grouped by kind */}
      {Object.entries(groupedByKind).map(([kind, items]) => (
        <div key={kind} className={styles.kindSection}>
          <Text
            variant="body-x-small"
            color="secondary"
            className={styles.sectionLabel}
          >
            {kind}
          </Text>
          <div className={styles.kindGrid}>
            {items.map(item => (
              <Tile
                key={`${item.kind}:${item.metadata.namespace || 'default'}/${item.metadata.name}`}
                href={`/catalog/${item.metadata.namespace || 'default'}/${item.kind.toLowerCase()}/${item.metadata.name}`}
                compact
                leading={kindIcons[kind] || <RiStackLine size={14} />}
                title={item.metadata.title || item.metadata.name}
              />
            ))}
          </div>
        </div>
      ))}
    </Flex>
  );
};

export const OwnershipCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: RELATION_OWNER_OF,
  });

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiBriefcaseLine size={18} className={styles.headerIcon} />
          <Text variant="title-small" weight="bold">
            {t('ownership.title')}
          </Text>
          {entities && entities.length > 0 && (
            <Text variant="body-x-small" className={styles.countBadge}>
              {entities.length}
            </Text>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        <OwnershipContent entities={entities} loading={loading} error={error} />
      </CardBody>
    </Card>
  );
};
